import { useEffect, useState } from 'react';
import { DataGrid, GridColDef, GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxiOutlined';
import ListAltIcon from '@mui/icons-material/ListAltOutlined';
import SearchIcon from '@mui/icons-material/Search';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoffOutlined';
import EventIcon from '@mui/icons-material/EventOutlined';
import PrintIcon from '@mui/icons-material/PrintOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import GroupAddIcon from '@mui/icons-material/GroupAddOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import PersonRemoveIcon from '@mui/icons-material/PersonRemoveOutlined';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BadgeIcon from '@mui/icons-material/BadgeOutlined';
import { useAuth } from '../../context/authContext';
import { StatTile } from '../dashboard/StatTile';
import { listLookup } from '../../api/masterData';
import {
  AssignableParams,
  assignApplicationsToBatch,
  BatchSize,
  bulkAssignFlight,
  bulkMarkReady,
  bulkSendEmail,
  bulkSendWhatsApp,
  createBatch,
  deleteBatch,
  getBatchProfilesPdfBlob,
  getJourney,
  getReadyToFlyDashboard,
  listAssignable,
  listBatches,
  listEligible,
  openAirportPickup,
  openBulkPrint,
  openDepartureList,
  openTravelManifest,
  removeApplicationFromBatch,
  updateBatch,
} from '../../api/readyToFly';
import {
  AssignableApplication,
  BatchOperationalSummary,
  BulkStatusResult,
  TravelBatch,
  TravelDetailInput,
} from '../../types/readyToFly';
import { TableToolbar } from '../../components/table/TableToolbar';
import { PageHeader } from '../../components/PageHeader';
import { BulkAssignFlightDialog } from './BulkAssignFlightDialog';
import { BulkEmailDialog, BulkWhatsAppDialog } from './BulkCommsDialogs';
import { TravelDetailDialog } from './TravelDetailDialog';
import { ApplicationDetailsDialog } from '../students/ApplicationDetailsDialog';
import { FullyPaidStudentsTable } from './FullyPaidStudentsTable';
import { FinalDocumentProgressChip } from '../final-documentation/FinalDocumentProgressChip';
import { FinalDocumentChecklistDialog } from '../final-documentation/FinalDocumentChecklistDialog';
import { MarkReadyReviewDialog } from './MarkReadyReviewDialog';
import {
  DocumentPreviewDialog,
  DocumentPreviewTarget,
} from '../../components/DocumentPreviewDialog';
import { downloadBlob } from '../../utils/downloadBlob';

const BATCH_SIZES: BatchSize[] = [15, 20, 50, 100];

const extractErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    return err.response?.data?.message ?? 'Something went wrong.';
  }
  return 'Something went wrong.';
};

const ReadyToFlyDashboardTiles = () => {
  const { hasPermission } = useAuth();
  const statsQuery = useQuery({
    queryKey: ['ready-to-fly-dashboard'],
    queryFn: getReadyToFlyDashboard,
    enabled: hasPermission('ready_to_fly', 'view'),
  });
  const s = statsQuery.data;

  return (
    <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
      <StatTile label="Students Ready" value={s?.studentsReady ?? '—'} color="warning.main" />
      <StatTile label="Flying This Week" value={s?.flyingThisWeek ?? '—'} color="info.main" />
      <StatTile label="Flying Today" value={s?.flyingToday ?? '—'} color="info.main" />
      <StatTile label="Arrived" value={s?.arrived ?? '—'} color="success.main" />
      <StatTile label="Pending Documents" value={s?.pendingDocuments ?? '—'} color="error.main" />
      <StatTile
        label="Students Without Batch"
        value={s?.studentsWithoutBatch ?? '—'}
        color="warning.main"
      />
    </Stack>
  );
};

/** Batch Operational Status (§19-§22) — a clear ✓/◐/○ indicator per
 * milestone, reusing the same percentages the batch summary already
 * computes server-side (never a separately hand-maintained status). */
const BatchSummaryBar = ({ summary }: { summary: BatchOperationalSummary }) => {
  const milestoneIcon = (percent: number) => (percent === 100 ? '✓' : percent > 0 ? '◐' : '○');
  const rows: Array<[string, { percent: number; completed: number; total: number }]> = [
    ['Offer Letter', summary.offerLetter],
    ['Invitation Letter', summary.invitationLetter],
    ['Visa', summary.visa],
    ['Flight', summary.flight],
    ['Departure', summary.departure],
  ];
  return (
    <Stack spacing={0.5} sx={{ mt: 1 }}>
      <Chip
        size="small"
        label={summary.overallStatus}
        color={
          summary.overallStatus === 'Completed'
            ? 'success'
            : summary.overallStatus === 'Ready for Departure'
              ? 'info'
              : summary.overallStatus === 'Departed'
                ? 'secondary'
                : 'default'
        }
        sx={{ alignSelf: 'flex-start', mb: 0.5 }}
      />
      {rows.map(([label, m]) => (
        <Stack key={label} direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" sx={{ width: 130 }}>
            {milestoneIcon(m.percent)} {label}
          </Typography>
          <Box sx={{ flexGrow: 1, maxWidth: 160 }}>
            <LinearProgress
              variant="determinate"
              value={m.percent}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {m.completed}/{m.total} ({m.percent}%)
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

/** Batch/Badge allocation (§15) manual student picker — searchable
 * checkbox list, backed by the new paginated /ready-to-fly/assignable
 * endpoint. Reused both for "assign into this batch" and, from within a
 * batch's own member list, "move to a different batch" (§17) — same
 * eligibility-checked backend action either way. */
const AssignStudentsDialog = ({
  batch,
  otherBatches,
  onClose,
}: {
  batch: TravelBatch | null;
  otherBatches: TravelBatch[];
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [universityId, setUniversityId] = useState<number | ''>('');
  const [countryId, setCountryId] = useState<number | ''>('');
  const [intakeId, setIntakeId] = useState<number | ''>('');
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const universitiesQuery = useQuery({
    queryKey: ['lookup', '/master-data/universities'],
    queryFn: () => listLookup('/master-data/universities', { activeOnly: true }),
    enabled: !!batch,
  });
  const countriesQuery = useQuery({
    queryKey: ['lookup', '/master-data/countries'],
    queryFn: () => listLookup('/master-data/countries', { activeOnly: true }),
    enabled: !!batch,
  });
  const intakesQuery = useQuery({
    queryKey: ['lookup', '/master-data/intakes'],
    queryFn: () => listLookup('/master-data/intakes', { activeOnly: true }),
    enabled: !!batch,
  });

  const assignableQuery = useQuery({
    queryKey: ['ready-to-fly-assignable', search, universityId, countryId, intakeId],
    queryFn: () =>
      listAssignable({
        page: 1,
        pageSize: 100,
        search: search || undefined,
        universityId: universityId || undefined,
        countryId: countryId || undefined,
        intakeId: intakeId || undefined,
      } as AssignableParams),
    enabled: !!batch,
  });

  const assignMutation = useMutation({
    mutationFn: (payload: { applicationIds: number[]; studentIds: number[] }) =>
      assignApplicationsToBatch(batch!.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['travel-batches'] });
      queryClient.invalidateQueries({ queryKey: ['ready-to-fly-assignable'] });
      queryClient.invalidateQueries({ queryKey: ['ready-to-fly-dashboard'] });
      setSelected([]);
      setError(null);
      onClose();
    },
    onError: (err) =>
      setError(
        isAxiosError(err)
          ? (err.response?.data?.message ?? 'Failed to assign students')
          : 'Failed to assign students',
      ),
  });

  // Batch eligibility correction (Phase 63) — a row may have no formal
  // Application yet (a pre-Application Registered student), so the
  // selection key can't always be an applicationId; `student:<id>` rows
  // are split out and sent as studentIds, letting the server provision
  // their Application at assignment time.
  const rowKey = (a: AssignableApplication) =>
    a.applicationId != null ? `app:${a.applicationId}` : `student:${a.studentId}`;
  const toggle = (key: string) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  const handleAssign = () => {
    const applicationIds = selected
      .filter((k) => k.startsWith('app:'))
      .map((k) => Number(k.slice(4)));
    const studentIds = selected
      .filter((k) => k.startsWith('student:'))
      .map((k) => Number(k.slice(8)));
    assignMutation.mutate({ applicationIds, studentIds });
  };

  return (
    <Dialog open={!!batch} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Assign Students {batch ? `— ${batch.batchNumber}` : ''}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            placeholder="Search student..."
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <TextField
              select
              size="small"
              label="University"
              sx={{ minWidth: 160 }}
              value={universityId}
              onChange={(e) => setUniversityId(e.target.value ? Number(e.target.value) : '')}
            >
              <MenuItem value="">All</MenuItem>
              {universitiesQuery.data?.map((u) => (
                <MenuItem key={u.id as number} value={u.id as number}>
                  {String(u.name)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Country"
              sx={{ minWidth: 140 }}
              value={countryId}
              onChange={(e) => setCountryId(e.target.value ? Number(e.target.value) : '')}
            >
              <MenuItem value="">All</MenuItem>
              {countriesQuery.data?.map((c) => (
                <MenuItem key={c.id as number} value={c.id as number}>
                  {String(c.name)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Intake"
              sx={{ minWidth: 140 }}
              value={intakeId}
              onChange={(e) => setIntakeId(e.target.value ? Number(e.target.value) : '')}
            >
              <MenuItem value="">All</MenuItem>
              {intakesQuery.data?.map((i) => (
                <MenuItem key={i.id as number} value={i.id as number}>
                  {String(i.name)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Shows every registered student with an approved registration payment who isn't already
            in a batch — Visa/Documents/Ready to Fly are shown for reference only and never hide a
            student from selection.
          </Typography>
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Student</TableCell>
                  <TableCell>University</TableCell>
                  <TableCell>Intake</TableCell>
                  <TableCell>Documents</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell>Visa</TableCell>
                  <TableCell>Ready to Fly</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignableQuery.data?.data.map((a) => {
                  const key = rowKey(a);
                  const currentBatch =
                    a.applicationId != null
                      ? otherBatches.find((b) =>
                          b.members.some((m) => m.application.id === a.applicationId),
                        )
                      : undefined;
                  return (
                    <TableRow
                      key={key}
                      hover
                      onClick={() => toggle(key)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.includes(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggle(key)}
                        />
                      </TableCell>
                      <TableCell>
                        {a.student.firstName} {a.student.lastName} ({a.student.studentNumber})
                        {a.applicationId == null && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            No Application yet — will be created on assignment
                          </Typography>
                        )}
                        {currentBatch && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            Currently in {currentBatch.batchNumber}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{a.university?.name ?? '—'}</TableCell>
                      <TableCell>{a.intake?.name ?? '—'}</TableCell>
                      <TableCell>
                        {a.documents ? `${a.documents.approved}/${a.documents.requiredTotal}` : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={a.payment.status}
                          color={
                            a.payment.status === 'PAID'
                              ? 'success'
                              : a.payment.status === 'PENDING'
                                ? 'warning'
                                : 'error'
                          }
                        />
                      </TableCell>
                      <TableCell>{a.visaStatus?.name ?? 'Not Applied'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={a.readyToFly ? 'Yes' : 'No'}
                          color={a.readyToFly ? 'success' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {assignableQuery.data?.data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                        No batch-eligible students found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={selected.length === 0 || assignMutation.isPending}
          onClick={handleAssign}
        >
          Assign Selected ({selected.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Batch detail view (§18/§40) — the batch's own student list with
 * document/visa/flight status columns, a "Move" action per student (§17),
 * and a click-through to the existing Student profile (never a duplicate
 * one, per §18's own instruction). */
const BatchDetailDialog = ({
  batch,
  allBatches,
  onClose,
}: {
  batch: TravelBatch | null;
  allBatches: TravelBatch[];
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [moveTarget, setMoveTarget] = useState<{ applicationId: number; toBatchId: number | '' }>({
    applicationId: 0,
    toBatchId: '',
  });
  const [editingDeparture, setEditingDeparture] = useState(false);
  const [departureDate, setDepartureDate] = useState('');
  const [editingCapacity, setEditingCapacity] = useState(false);
  const [capacity, setCapacity] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { hasPermission } = useAuth();
  const canManage = hasPermission('ready_to_fly', 'manage');
  // Batch progress correction (Phase 64) — "Edit progress" reuses the
  // existing per-application editor (Offer Letter/Invitation/Visa/
  // Timeline), so each batch member can progress independently without
  // leaving Ready to Fly.
  const [progressApplicationId, setProgressApplicationId] = useState<number | null>(null);

  const moveMutation = useMutation({
    mutationFn: ({ toBatchId, applicationId }: { toBatchId: number; applicationId: number }) =>
      assignApplicationsToBatch(toBatchId, { applicationIds: [applicationId], studentIds: [] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['travel-batches'] });
      setMoveTarget({ applicationId: 0, toBatchId: '' });
      setError(null);
    },
    onError: (err) =>
      setError(
        isAxiosError(err)
          ? (err.response?.data?.message ?? 'Failed to move student')
          : 'Failed to move student',
      ),
  });

  const removeMutation = useMutation({
    mutationFn: (applicationId: number) => removeApplicationFromBatch(batch!.id, applicationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['travel-batches'] });
      queryClient.invalidateQueries({ queryKey: ['ready-to-fly-dashboard'] });
      setError(null);
    },
    onError: (err) =>
      setError(
        isAxiosError(err)
          ? (err.response?.data?.message ?? 'Failed to remove student')
          : 'Failed to remove student',
      ),
  });

  const updateDepartureMutation = useMutation({
    mutationFn: (nextDate: string) => updateBatch(batch!.id, { departureDate: nextDate || null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['travel-batches'] });
      setEditingDeparture(false);
      setError(null);
    },
    onError: (err) =>
      setError(
        isAxiosError(err)
          ? (err.response?.data?.message ?? 'Failed to update departure date')
          : 'Failed to update departure date',
      ),
  });

  const updateCapacityMutation = useMutation({
    mutationFn: (next: string) => updateBatch(batch!.id, { capacity: next ? Number(next) : null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['travel-batches'] });
      setEditingCapacity(false);
      setError(null);
    },
    onError: (err) =>
      setError(
        isAxiosError(err)
          ? (err.response?.data?.message ?? 'Failed to update capacity')
          : 'Failed to update capacity',
      ),
  });

  if (!batch) return null;
  const otherBatches = allBatches.filter((b) => b.id !== batch.id);

  return (
    <>
      <Dialog open={!!batch} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {batch.batchNumber} {batch.name ? `— ${batch.name}` : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {batch.members.length} student(s)
              </Typography>
              {editingDeparture ? (
                <>
                  <TextField
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={departureDate}
                    onChange={(e) => setDepartureDate(e.target.value)}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    disabled={updateDepartureMutation.isPending}
                    onClick={() => updateDepartureMutation.mutate(departureDate)}
                  >
                    Save
                  </Button>
                  <Button size="small" onClick={() => setEditingDeparture(false)}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    · Departure:{' '}
                    {batch.departureDate
                      ? new Date(batch.departureDate).toLocaleDateString()
                      : 'Not set'}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      setDepartureDate(batch.departureDate ? batch.departureDate.slice(0, 10) : '');
                      setEditingDeparture(true);
                    }}
                  >
                    {batch.departureDate ? 'Change' : 'Set'}
                  </Button>
                </>
              )}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {editingCapacity ? (
                <>
                  <TextField
                    type="number"
                    size="small"
                    label="Capacity"
                    sx={{ width: 140 }}
                    inputProps={{ min: 1 }}
                    helperText="Empty = no limit"
                    value={capacity}
                    onChange={(e) => setCapacity(e.target.value)}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    disabled={updateCapacityMutation.isPending}
                    onClick={() => updateCapacityMutation.mutate(capacity)}
                  >
                    Save
                  </Button>
                  <Button size="small" onClick={() => setEditingCapacity(false)}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Capacity:{' '}
                    {batch.capacity != null
                      ? `${batch.members.length} / ${batch.capacity}`
                      : 'No limit'}
                  </Typography>
                  {canManage && (
                    <Button
                      size="small"
                      onClick={() => {
                        setCapacity(batch.capacity != null ? String(batch.capacity) : '');
                        setEditingCapacity(true);
                      }}
                    >
                      {batch.capacity != null ? 'Change' : 'Set'}
                    </Button>
                  )}
                </>
              )}
            </Stack>
            <BatchSummaryBar summary={batch.summary} />
            <Divider />
            <List dense>
              {batch.members.map((m) => (
                <ListItem
                  key={m.id}
                  divider
                  secondaryAction={
                    moveTarget.applicationId === m.application.id ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                          select
                          size="small"
                          sx={{ width: 140 }}
                          value={moveTarget.toBatchId}
                          onChange={(e) =>
                            setMoveTarget((p) => ({
                              ...p,
                              toBatchId: e.target.value ? Number(e.target.value) : '',
                            }))
                          }
                        >
                          {otherBatches.map((b) => (
                            <MenuItem key={b.id} value={b.id}>
                              {b.batchNumber}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!moveTarget.toBatchId || moveMutation.isPending}
                          onClick={() =>
                            moveTarget.toBatchId &&
                            moveMutation.mutate({
                              applicationId: m.application.id,
                              toBatchId: moveTarget.toBatchId,
                            })
                          }
                        >
                          Move
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setMoveTarget({ applicationId: 0, toBatchId: '' })}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Edit progress (Offer Letter/Invitation/Visa)">
                          <IconButton
                            size="small"
                            onClick={() => setProgressApplicationId(m.application.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Open student profile">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(`/students/${m.application.student.id}?tab=academic`)
                            }
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {otherBatches.length > 0 && (
                          <Button
                            size="small"
                            onClick={() =>
                              setMoveTarget({ applicationId: m.application.id, toBatchId: '' })
                            }
                          >
                            Move
                          </Button>
                        )}
                        <Tooltip title="Remove from this batch">
                          <IconButton
                            size="small"
                            disabled={removeMutation.isPending}
                            onClick={() => removeMutation.mutate(m.application.id)}
                          >
                            <PersonRemoveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )
                  }
                >
                  <ListItemText
                    primary={`${m.application.student.firstName} ${m.application.student.lastName} (${m.application.student.studentNumber})`}
                    secondary={`${m.application.university.name}, ${m.application.university.country.name}`}
                  />
                </ListItem>
              ))}
              {batch.members.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No students assigned yet.
                </Typography>
              )}
            </List>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <ApplicationDetailsDialog
        applicationId={progressApplicationId}
        onClose={() => setProgressApplicationId(null)}
      />
    </>
  );
};

const StudentJourneyPanel = () => {
  const queryClient = useQueryClient();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [detailApplicationId, setDetailApplicationId] = useState<number | null>(null);

  const [assignFlightOpen, setAssignFlightOpen] = useState(false);
  const [assignFlightError, setAssignFlightError] = useState<string | null>(null);
  const [assignFlightResult, setAssignFlightResult] = useState<BulkStatusResult | null>(null);

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailResult, setEmailResult] = useState<{
    total: number;
    sent: number;
    failed: number;
  } | null>(null);

  const [whatsAppOpen, setWhatsAppOpen] = useState(false);
  const [whatsAppError, setWhatsAppError] = useState<string | null>(null);
  const [whatsAppResult, setWhatsAppResult] = useState<{
    total: number;
    sent: number;
    failed: number;
  } | null>(null);

  const [markReadyMessage, setMarkReadyMessage] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [checklistStudent, setChecklistStudent] = useState<{ id: number; name: string } | null>(
    null,
  );

  const journeyQuery = useQuery({
    queryKey: ['journey', paginationModel.page, paginationModel.pageSize, search],
    queryFn: () =>
      getJourney({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: search || undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['journey'] });
    queryClient.invalidateQueries({ queryKey: ['ready-to-fly-dashboard'] });
  };

  const markReadyMutation = useMutation({
    mutationFn: () => bulkMarkReady(selection as number[]),
    onSuccess: (result) => {
      setMarkReadyMessage(
        `Marked ${result.succeeded}/${result.total} Ready to Fly (${result.failed} failed)`,
      );
      setSelection([]);
      setReviewOpen(false);
      invalidateAll();
    },
  });

  const assignFlightMutation = useMutation({
    mutationFn: (input: TravelDetailInput & { departureDate?: string; arrivalDate?: string }) =>
      bulkAssignFlight({ applicationIds: selection as number[], ...input }),
    onSuccess: (result) => {
      setAssignFlightResult(result);
      setAssignFlightError(null);
      invalidateAll();
    },
    onError: (err) => setAssignFlightError(extractErrorMessage(err)),
  });

  const emailMutation = useMutation({
    mutationFn: (input: { templateId?: number; subject?: string; body?: string }) =>
      bulkSendEmail({ applicationIds: selection as number[], ...input }),
    onSuccess: (result) => {
      setEmailResult(result);
      setEmailError(null);
    },
    onError: (err) => setEmailError(extractErrorMessage(err)),
  });

  const whatsAppMutation = useMutation({
    mutationFn: (input: { templateId?: number; body?: string }) =>
      bulkSendWhatsApp({ applicationIds: selection as number[], ...input }),
    onSuccess: (result) => {
      setWhatsAppResult(result);
      setWhatsAppError(null);
    },
    onError: (err) => setWhatsAppError(extractErrorMessage(err)),
  });

  const columns: GridColDef[] = [
    {
      field: 'studentNumber',
      headerName: 'Student #',
      width: 110,
      valueGetter: (_v, row) => row.student.studentNumber,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      valueGetter: (_v, row) => `${row.student.firstName} ${row.student.lastName}`,
    },
    {
      field: 'university',
      headerName: 'University',
      width: 180,
      valueGetter: (_v, row) => row.university.name,
    },
    // Student Journey fix (Phase 65) — §"Show Student, University, Batch,
    // Visa, Flight, Departure and Status": surfaces the existing
    // TravelBatchMember relation directly, never a new field.
    {
      field: 'batch',
      headerName: 'Batch',
      width: 110,
      valueGetter: (_v, row) => row.travelBatchMember?.travelBatch.batchNumber ?? '—',
    },
    {
      field: 'visa',
      headerName: 'Visa',
      width: 110,
      valueGetter: (_v, row) => row.visaStatus?.name ?? '—',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => <Chip size="small" label={String(params.value).replace(/_/g, ' ')} />,
    },
    {
      field: 'airline',
      headerName: 'Flight',
      width: 150,
      valueGetter: (_v, row) =>
        row.travelDetail?.airline
          ? `${row.travelDetail.airline} ${row.travelDetail.flightNumber ?? ''}`
          : '—',
    },
    {
      field: 'departureDate',
      headerName: 'Departure',
      width: 150,
      valueGetter: (_v, row) =>
        row.departureDate ? new Date(row.departureDate).toLocaleString() : '—',
    },
    // Final Documentation Checklist (Phase 95) — review before Ready to Fly.
    {
      field: 'finalDocumentation',
      headerName: 'Final docs',
      width: 200,
      sortable: false,
      renderCell: (params) =>
        params.row.finalDocumentation ? (
          <FinalDocumentProgressChip
            progress={params.row.finalDocumentation}
            onClick={() =>
              setChecklistStudent({
                id: params.row.student.id,
                name: `${params.row.student.firstName} ${params.row.student.lastName}`,
              })
            }
          />
        ) : (
          '—'
        ),
    },
    {
      field: 'pickup',
      headerName: 'Pickup',
      width: 110,
      valueGetter: (_v, row) =>
        row.travelDetail?.pickupRequired
          ? row.travelDetail?.pickupAssigned
            ? 'Assigned'
            : 'Required'
          : '—',
    },
  ];

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
        flexWrap="wrap"
        gap={1}
      >
        <TextField
          placeholder="Search student name or #…"
          size="small"
          sx={{ width: 260 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPaginationModel((p) => ({ ...p, page: 0 }));
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
          {selection.length > 0 && (
            <>
              <Button
                size="small"
                startIcon={<FlightTakeoffIcon />}
                disabled={markReadyMutation.isPending}
                onClick={() => {
                  setMarkReadyMessage(null);
                  setReviewOpen(true);
                }}
              >
                Ready to Fly ({selection.length})
              </Button>
              <Button
                size="small"
                startIcon={<EventIcon />}
                onClick={() => setAssignFlightOpen(true)}
              >
                Assign Flight ({selection.length})
              </Button>
              <Button
                size="small"
                startIcon={<PrintIcon />}
                onClick={() => openBulkPrint(selection as number[])}
              >
                Print ({selection.length})
              </Button>
              <Button
                size="small"
                startIcon={<EmailIcon />}
                onClick={() => {
                  setEmailError(null);
                  setEmailResult(null);
                  setEmailOpen(true);
                }}
              >
                Email ({selection.length})
              </Button>
              <Button
                size="small"
                startIcon={<WhatsAppIcon />}
                onClick={() => {
                  setWhatsAppError(null);
                  setWhatsAppResult(null);
                  setWhatsAppOpen(true);
                }}
              >
                WhatsApp ({selection.length})
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {markReadyMessage && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMarkReadyMessage(null)}>
          {markReadyMessage}
        </Alert>
      )}

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={journeyQuery.data?.data ?? []}
          columns={columns}
          rowCount={journeyQuery.data?.meta.total ?? 0}
          loading={journeyQuery.isFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
          slots={{ toolbar: TableToolbar }}
          checkboxSelection
          rowSelectionModel={selection}
          onRowSelectionModelChange={setSelection}
          onRowClick={(params) => setDetailApplicationId(params.row.id)}
        />
      </Box>

      <MarkReadyReviewDialog
        open={reviewOpen}
        applications={(journeyQuery.data?.data ?? []).filter((a) => selection.includes(a.id))}
        pending={markReadyMutation.isPending}
        onConfirm={() => markReadyMutation.mutate()}
        onClose={() => setReviewOpen(false)}
      />
      <FinalDocumentChecklistDialog
        student={checklistStudent}
        onClose={() => setChecklistStudent(null)}
      />

      <BulkAssignFlightDialog
        open={assignFlightOpen}
        applicationCount={selection.length}
        errorMessage={assignFlightError}
        resultSummary={assignFlightResult}
        onClose={() => setAssignFlightOpen(false)}
        onSubmit={(input) => assignFlightMutation.mutateAsync(input).then(() => undefined)}
      />
      <BulkEmailDialog
        open={emailOpen}
        studentCount={selection.length}
        errorMessage={emailError}
        resultSummary={emailResult}
        onClose={() => setEmailOpen(false)}
        onSubmit={(input) => emailMutation.mutateAsync(input).then(() => undefined)}
      />
      <BulkWhatsAppDialog
        open={whatsAppOpen}
        studentCount={selection.length}
        errorMessage={whatsAppError}
        resultSummary={whatsAppResult}
        onClose={() => setWhatsAppOpen(false)}
        onSubmit={(input) => whatsAppMutation.mutateAsync(input).then(() => undefined)}
      />
      <TravelDetailDialog
        applicationId={detailApplicationId}
        onClose={() => setDetailApplicationId(null)}
      />
    </Box>
  );
};

const BatchesPanel = ({ initialBatchId }: { initialBatchId: number | null }) => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('ready_to_fly', 'manage');
  const queryClient = useQueryClient();
  const [limit, setLimit] = useState<BatchSize>(15);
  const [createOpen, setCreateOpen] = useState(false);
  const [batchName, setBatchName] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [capacity, setCapacity] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [assignBatchId, setAssignBatchId] = useState<number | null>(null);
  // `?batch=<id>` (e.g. from a notification) opens that batch directly.
  const [detailBatchId, setDetailBatchId] = useState<number | null>(initialBatchId);
  useEffect(() => {
    if (initialBatchId) setDetailBatchId(initialBatchId);
  }, [initialBatchId]);
  const [profilesTarget, setProfilesTarget] = useState<DocumentPreviewTarget | null>(null);
  const openProfiles = (batch: TravelBatch) => {
    const filename = `${batch.batchNumber}-student-profiles.pdf`;
    setProfilesTarget({
      key: `profiles-${batch.id}-${Date.now()}`,
      originalName: filename,
      mimeType: 'application/pdf',
      fetchBlob: () => getBatchProfilesPdfBlob(batch.id),
      onDownload: () => {
        getBatchProfilesPdfBlob(batch.id)
          .then((blob) => downloadBlob(blob, filename))
          .catch((err) =>
            window.alert(err instanceof Error ? err.message : 'Failed to download the PDF'),
          );
      },
    });
  };

  const eligibleQuery = useQuery({
    queryKey: ['eligible', limit],
    queryFn: () => listEligible(limit),
  });
  const batchesQuery = useQuery({ queryKey: ['travel-batches'], queryFn: listBatches });
  const allBatches = batchesQuery.data ?? [];
  // Derived by id (not stored directly) so the dialogs always reflect the
  // latest data after an assign/move mutation invalidates this query.
  const assignBatch = allBatches.find((b) => b.id === assignBatchId) ?? null;
  const detailBatch = allBatches.find((b) => b.id === detailBatchId) ?? null;

  const createMutation = useMutation({
    mutationFn: () =>
      createBatch({
        name: batchName || undefined,
        departureDate: departureDate || undefined,
        capacity: capacity ? Number(capacity) : undefined,
      }),
    onSuccess: (batch) => {
      queryClient.invalidateQueries({ queryKey: ['travel-batches'] });
      setCreateOpen(false);
      setBatchName('');
      setDepartureDate('');
      setCapacity('');
      setError(null);
      // Manual Batch allocation (Phase 61) — a new batch starts empty, so
      // go straight into the picker rather than leaving the Officer to
      // hunt for the "Assign students" icon on the card they just made.
      setAssignBatchId(batch.id);
    },
    onError: (err) =>
      setError(
        isAxiosError(err)
          ? (err.response?.data?.message ?? 'Failed to create batch')
          : 'Failed to create batch',
      ),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteBatch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eligible'] });
      queryClient.invalidateQueries({ queryKey: ['travel-batches'] });
    },
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1">Travel batches</Typography>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            New batch
          </Button>
        )}
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Show up to"
          sx={{ width: 140 }}
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value) as BatchSize)}
        >
          {BATCH_SIZES.map((n) => (
            <MenuItem key={n} value={n}>
              {n} students
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="body2" color="text.secondary">
          {eligibleQuery.data?.length ?? 0} student(s) currently eligible (Registered, Registration
          Fee approved, not yet in a batch — Visa/Documents/Ready to Fly are separate progress
          milestones, never a requirement here) — for reference only; use "Assign students" on a
          batch to actually select them.
        </Typography>
      </Stack>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Eligible students
      </Typography>
      <Stack spacing={1} sx={{ mb: 3 }}>
        {eligibleQuery.data?.map((a) => (
          <Card key={a.applicationId ?? `student-${a.studentId}`} variant="outlined">
            <CardContent sx={{ py: '12px !important' }}>
              <Typography variant="body2">
                {a.student.firstName} {a.student.lastName} ({a.student.studentNumber})
                {a.university && ` — ${a.university.name}, ${a.university.country.name}`}
              </Typography>
            </CardContent>
          </Card>
        ))}
        {eligibleQuery.data?.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No eligible students right now.
          </Typography>
        )}
      </Stack>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Travel batches
      </Typography>
      <Stack spacing={1.5}>
        {batchesQuery.data?.map((batch) => (
          <Card key={batch.id} variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">{batch.batchNumber}</Typography>
                    {batch.name && <Chip size="small" label={batch.name} />}
                    <Chip
                      size="small"
                      color={
                        batch.capacity != null && batch.members.length >= batch.capacity
                          ? 'warning'
                          : 'default'
                      }
                      label={
                        batch.capacity != null
                          ? `${batch.members.length}/${batch.capacity} students`
                          : `${batch.members.length} students`
                      }
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {batch.departureDate
                      ? `Departure: ${new Date(batch.departureDate).toLocaleDateString()} · `
                      : ''}
                    Created by {batch.createdBy.firstName} {batch.createdBy.lastName}
                  </Typography>
                  <BatchSummaryBar summary={batch.summary} />
                </Box>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="View batch">
                    <IconButton onClick={() => setDetailBatchId(batch.id)}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  {canManage && (
                    <Tooltip title="Assign students">
                      <IconButton onClick={() => setAssignBatchId(batch.id)}>
                        <GroupAddIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Student profiles (PDF)">
                    <IconButton
                      aria-label="student profiles pdf"
                      onClick={() => openProfiles(batch)}
                    >
                      <BadgeIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Departure list">
                    <IconButton onClick={() => openDepartureList(batch.id)}>
                      <ListAltIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Airport pickup">
                    <IconButton onClick={() => openAirportPickup(batch.id)}>
                      <LocalTaxiIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Travel manifest">
                    <IconButton onClick={() => openTravelManifest(batch.id)}>
                      <DescriptionIcon />
                    </IconButton>
                  </Tooltip>
                  {canManage && (
                    <Tooltip title="Delete batch">
                      <IconButton onClick={() => deleteMutation.mutate(batch.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
        {batchesQuery.data?.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No travel batches yet.
          </Typography>
        )}
      </Stack>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create travel batch</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Typography variant="body2" color="text.secondary">
              Creates an empty batch. You choose which specific students to assign to it next.
            </Typography>
            <TextField
              label="Name (optional)"
              fullWidth
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
            />
            <TextField
              label="Departure date (optional)"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={departureDate}
              onChange={(e) => setDepartureDate(e.target.value)}
            />
            <TextField
              label="Capacity (optional)"
              type="number"
              fullWidth
              inputProps={{ min: 1 }}
              helperText="Maximum number of students. Leave empty for no limit."
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? 'Creating…' : 'Create batch'}
          </Button>
        </DialogActions>
      </Dialog>

      <AssignStudentsDialog
        batch={assignBatch}
        otherBatches={allBatches}
        onClose={() => setAssignBatchId(null)}
      />
      <BatchDetailDialog
        batch={detailBatch}
        allBatches={allBatches}
        onClose={() => setDetailBatchId(null)}
      />
      {detailBatchId != null && batchesQuery.isSuccess && !detailBatch && (
        <Alert severity="info" sx={{ mt: 2 }} onClose={() => setDetailBatchId(null)}>
          That travel batch no longer exists.
        </Alert>
      )}
      <DocumentPreviewDialog target={profilesTarget} onClose={() => setProfilesTarget(null)} />
    </Box>
  );
};

const TAB_KEYS = ['journey', 'batches', 'fully-paid'];

export const ReadyToFlyPage = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const batchParam = Number(searchParams.get('batch')) || null;
  const [tab, setTab] = useState(() => Math.max(TAB_KEYS.indexOf(tabParam ?? ''), 0));
  useEffect(() => {
    const index = TAB_KEYS.indexOf(tabParam ?? '');
    if (index >= 0) setTab(index);
  }, [tabParam, batchParam]);

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={FlightTakeoffIcon}
        color="#F5A623"
        title="Ready to Fly"
        subtitle="Manage the student journey to departure and travel batches."
      />

      <ReadyToFlyDashboardTiles />

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Student Journey" />
        <Tab label="Batches" />
        <Tab label="Fully Paid" />
      </Tabs>

      {tab === 0 && <StudentJourneyPanel />}
      {tab === 1 && <BatchesPanel initialBatchId={batchParam} />}
      {tab === 2 && <FullyPaidStudentsTable variant="readyToFly" />}
    </Box>
  );
};
